import React, { useState, useEffect } from 'react';
import { Moon, Sun, Clock, Globe } from 'lucide-react';

// Moon phase calculation
const getMoonPhase = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  
  // Calculate moon phase using synodic month
  let c = 0;
  let e = 0;
  let jd = 0;
  let b = 0;
  
  if (month < 3) {
    c = year - 1;
    e = month + 12;
  } else {
    c = year;
    e = month;
  }
  
  jd = Math.floor(365.25 * c) + Math.floor(30.6001 * (e + 1)) + day - 694039.09;
  jd /= 29.5305882;
  b = parseInt(jd);
  jd -= b;
  const phase = Math.round(jd * 8);
  
  const phases = [
    { name: 'New Moon', icon: '🌑', energy: 'New beginnings, setting intentions', shopping: 'Ideal for cleansing crystals' },
    { name: 'Waxing Crescent', icon: '🌒', energy: 'Growth, momentum building', shopping: 'Attract abundance stones' },
    { name: 'First Quarter', icon: '🌓', energy: 'Action, decision making', shopping: 'Power crystals recommended' },
    { name: 'Waxing Gibbous', icon: '🌔', energy: 'Refinement, patience', shopping: 'Heart chakra stones' },
    { name: 'Full Moon', icon: '🌕', energy: 'Manifestation, peak energy', shopping: 'Charge all your crystals!' },
    { name: 'Waning Gibbous', icon: '🌖', energy: 'Gratitude, sharing', shopping: 'Releasing crystals' },
    { name: 'Last Quarter', icon: '🌗', energy: 'Release, letting go', shopping: 'Protection stones' },
    { name: 'Waning Crescent', icon: '🌘', energy: 'Rest, reflection', shopping: 'Meditation crystals' },
  ];
  
  return phases[phase % 8];
};

// Get moon illumination percentage
const getMoonIllumination = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  
  // Simplified calculation
  const lp = 2551443; // Lunar period in seconds
  const now = new Date(year, month - 1, day, 20, 35, 0);
  const new_moon = new Date(1970, 0, 7, 20, 35, 0);
  const phase = ((now.getTime() - new_moon.getTime()) / 1000) % lp;
  const illumination = (1 - Math.cos(2 * Math.PI * phase / lp)) / 2;
  
  return Math.round(illumination * 100);
};

const MoonClock = () => {
  const [time, setTime] = useState(new Date());
  const [moonPhase, setMoonPhase] = useState(getMoonPhase());
  const [illumination, setIllumination] = useState(getMoonIllumination());
  const [timezone, setTimezone] = useState('');

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    // Get timezone
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimezone(tz);

    // Update moon phase daily
    const moonTimer = setInterval(() => {
      setMoonPhase(getMoonPhase());
      setIllumination(getMoonIllumination());
    }, 3600000); // Every hour

    return () => {
      clearInterval(timer);
      clearInterval(moonTimer);
    };
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-gradient-to-br from-[#1e1919] to-[#2a2525] rounded-2xl p-6 text-white" data-testid="moon-clock">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Moon className="w-5 h-5 text-[#c9a96e]" />
        <h3 className="font-serif text-lg">Lunar Clock</h3>
      </div>

      {/* Moon Phase Display */}
      <div className="text-center mb-6">
        <div className="text-6xl mb-2">{moonPhase.icon}</div>
        <h4 className="font-serif text-xl text-[#c9a96e] mb-1">{moonPhase.name}</h4>
        <p className="text-sm text-white/60">{illumination}% illuminated</p>
      </div>

      {/* Moon Energy */}
      <div className="bg-white/5 rounded-xl p-4 mb-4">
        <p className="text-xs uppercase tracking-wider text-[#c9a96e] mb-1">Lunar Energy</p>
        <p className="text-sm text-white/80">{moonPhase.energy}</p>
        <p className="text-xs text-[#9D4EDD] mt-2 flex items-center gap-1">
          <span>✨</span> {moonPhase.shopping}
        </p>
      </div>

      {/* Time Display */}
      <div className="border-t border-white/10 pt-4">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-4 h-4 text-[#FF6B9D]" />
          <span className="text-2xl font-mono font-bold">{formatTime(time)}</span>
        </div>
        <p className="text-sm text-white/60 mb-1">{formatDate(time)}</p>
        <div className="flex items-center gap-1 text-xs text-white/40">
          <Globe className="w-3 h-3" />
          <span>{timezone}</span>
        </div>
      </div>

      {/* World Times */}
      <div className="mt-4 pt-4 border-t border-white/10">
        <p className="text-xs uppercase tracking-wider text-[#c9a96e] mb-3">Crystal Energy Zones</p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {[
            { city: 'Los Angeles', tz: 'America/Los_Angeles', crystal: '💜' },
            { city: 'New York', tz: 'America/New_York', crystal: '💗' },
            { city: 'London', tz: 'Europe/London', crystal: '🤍' },
            { city: 'Tokyo', tz: 'Asia/Tokyo', crystal: '🔮' },
          ].map((zone) => (
            <div key={zone.city} className="bg-white/5 rounded-lg p-2">
              <div className="flex items-center gap-1 mb-1">
                <span>{zone.crystal}</span>
                <span className="text-white/60">{zone.city}</span>
              </div>
              <span className="font-mono text-white/80">
                {time.toLocaleTimeString('en-US', {
                  timeZone: zone.tz,
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MoonClock;
